<app-page size="narrow">
    <app-breadcrumbs [crumbs]="crumbs"></app-breadcrumbs>

    @if (form() && values(); as v) {
        <div [formGroup]="form()" class="sections">
            <div class="summary">
                <div class="summary-label">Samenvatting</div>
                <p>
                    @for (part of summary(); track $index) {
                        <span [class.value]="part.bold">{{ part.text }}</span>
                    }
                </p>
            </div>

            <app-section [columns]="2" description="Welke URL, onder welk platform en in welke omgeving." icon="bullseye" title="Wat wordt gecontroleerd">
                <app-field for="name" label="Naam">
                    <mat-form-field>
                        <input formControlName="name" id="name" matInput name="name" placeholder="Bijv. Backup-API health">
                        <mat-hint>Herkenbare naam in overzichten en meldingen.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field label="Platform">
                    <ng-select [items]="platforms()" [searchable]="true" bindLabel="name" bindValue="id" formControlName="platform_id"></ng-select>
                    <span hint>Platform waar deze check onder valt.</span>
                </app-field>
                <app-field for="url" full label="URL">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="url" id="url" matInput name="url" placeholder="https://">
                        <mat-hint>Het adres dat periodiek wordt opgevraagd.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field label="Niveau">
                    <ng-select [searchable]="true" bindLabel="name" bindValue="id" formControlName="level">
                        <ng-option [value]="checkpointLevels.PRODUCTION">{{ checkpointLevels.PRODUCTION }}</ng-option>
                        <ng-option [value]="checkpointLevels.HEALTH">{{ checkpointLevels.HEALTH }}</ng-option>
                        <ng-option [value]="checkpointLevels.CHECK">{{ checkpointLevels.CHECK }}</ng-option>
                        <ng-option [value]="checkpointLevels.ACCEPTANCE">{{ checkpointLevels.ACCEPTANCE }}</ng-option>
                    </ng-select>
                    <span hint>Bepaalt onder welk van de vier statusbolletjes in de navigatie deze check meetelt.</span>
                </app-field>
                <app-field for="external_ip_allowed" label="Externe IP" optional>
                    <mat-form-field>
                        <input class="font-monospace" formControlName="external_ip_allowed" id="external_ip_allowed" matInput name="external_ip_allowed" placeholder="0.0.0.0">
                        <mat-hint>Gevuld = de check wordt niet zelf uitgevoerd. Resultaten komen alleen binnen via externe logging vanaf dit IP.</mat-hint>
                    </mat-form-field>
                </app-field>
            </app-section>

            <app-section [columns]="3" description="Hoe vaak de check draait en in welke periode." icon="clock" title="Wanneer">
                <app-field for="interval" label="Interval">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="interval" id="interval" matInput name="interval" type="number">
                        <span class="unit" matTextSuffix>ms</span>
                        <mat-hint>Elke <b>{{ v.interval | formatMs }}</b> wordt de URL opgevraagd.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="start_date" label="Startdatum">
                    <mat-form-field>
                        <input (focus)="pickerStart.open()" [matDatepicker]="pickerStart" [min]="minDate" formControlName="start_date" id="start_date" matInput placeholder="Startdatum">
                        <mat-datepicker-toggle [for]="pickerStart" matSuffix></mat-datepicker-toggle>
                        <mat-datepicker #pickerStart></mat-datepicker>
                        <mat-hint>Vanaf deze dag actief.</mat-hint>
                        <mat-error [control]="form().get('start_date')"></mat-error>
                    </mat-form-field>
                </app-field>
                <app-field for="end_date" label="Einddatum" optional>
                    <mat-form-field>
                        <input (focus)="pickerEnd.open()" [matDatepicker]="pickerEnd" [min]="minDate" formControlName="end_date" id="end_date" matInput placeholder="Einddatum">
                        <mat-datepicker-toggle [for]="pickerEnd" matSuffix></mat-datepicker-toggle>
                        <mat-datepicker #pickerEnd></mat-datepicker>
                        <mat-hint>Leeg laten = blijft actief.</mat-hint>
                        <mat-error [control]="form().get('end_date')"></mat-error>
                    </mat-form-field>
                </app-field>
            </app-section>

            <app-section [columns]="3" description="Aan welke voorwaarden de respons moet voldoen." icon="circle-check" title="Wanneer is het goed">
                <app-field for="valid_response_code" label="Valide statuscode">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="valid_response_code" id="valid_response_code" matInput name="valid_response_code">
                        <mat-hint>HTTP-code die als goed telt. Elke andere code geeft een error.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="warning_response_code" label="Waarschuwings-statuscode" optional>
                    <mat-form-field>
                        <input class="font-monospace" formControlName="warning_response_code" id="warning_response_code" matInput name="warning_response_code" placeholder="Bijv. 301">
                        <mat-hint>Deze code geeft een waarschuwing in plaats van een error.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field label="SSL-certificaat controleren">
                    <ng-select [clearable]="false" [searchable]="false" bindLabel="name" bindValue="id" formControlName="validate_ssl">
                        <ng-option [value]="true">Ja</ng-option>
                        <ng-option [value]="false">Nee</ng-option>
                    </ng-select>
                    <span hint>Bij Ja geeft een ongeldig of verlopen certificaat een error.</span>
                </app-field>
                <app-field for="valid_checksum" full label="Checksum van de inhoud" optional>
                    <mat-form-field>
                        <input class="font-monospace" formControlName="valid_checksum" id="valid_checksum" matInput name="valid_checksum" placeholder="MD5-hash">
                        <mat-hint>MD5-hash van de verwachte inhoud. Komt de respons hier niet mee overeen, dan volgt een waarschuwing.</mat-hint>
                    </mat-form-field>
                </app-field>
            </app-section>

            <app-section [columns]="3" description="Hoe lang er gewacht wordt en hoe vaak opnieuw geprobeerd wordt na een error." icon="triangle-exclamation" title="Als het misgaat">
                <app-field for="timeout" label="Timeout">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="timeout" id="timeout" matInput name="timeout" type="number">
                        <span class="unit" matTextSuffix>ms</span>
                        <mat-hint>Geen antwoord binnen <b>{{ v.timeout | formatMs }}</b> = poging mislukt.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="retries" label="Pogingen">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="retries" id="retries" matInput name="retries" type="number">
                        <mat-hint>Aantal extra pogingen na een error, voordat het gewone interval weer geldt.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="retry_delay" label="Wachttijd tussen pogingen">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="retry_delay" id="retry_delay" matInput name="retry_delay" type="number">
                        <span class="unit" matTextSuffix>ms</span>
                        <mat-hint><b>{{ v.retry_delay | formatMs }}</b> wachten voor de volgende poging.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field label="Respons altijd opslaan">
                    <ng-select [clearable]="false" [searchable]="false" bindLabel="name" bindValue="id" formControlName="save_contents_on_success">
                        <ng-option [value]="false">Nee</ng-option>
                        <ng-option [value]="true">Ja</ng-option>
                    </ng-select>
                    <span hint>Bij een error wordt de respons altijd bewaard. Bij Ja ook een goede respons; handig bij debuggen.</span>
                </app-field>
                <div class="ui-info-block">
                    Tijdlijn bij een storing: de eerste mislukte poging zet de check direct op error. Daarna
                    {{ v.retries || 0 }}× opnieuw ({{ v.retry_delay | formatMs }} pauze + max. {{ v.timeout | formatMs }} wachten)
                    ≈ alle pogingen klaar na maximaal {{ timelineTotal() | formatMs }}.
                </div>
            </app-section>

            @for (range of ranges(); track range.nr) {
                <app-section [columns]="4" [description]="range.description" [icon]="range.icon" [title]="range.title">
                    <app-threshold-bar></app-threshold-bar>
                    @for (field of range.fields; track field.key) {
                        <app-field [for]="field.key" [label]="field.label">
                            <mat-form-field>
                                <input [formControlName]="field.key" [id]="field.key" [name]="field.key" class="font-monospace limit" matInput placeholder="—" type="number">
                                <span class="unit" matTextSuffix>{{ range.unit }}</span>
                                <mat-hint>{{ field.help }}</mat-hint>
                            </mat-form-field>
                        </app-field>
                    }
                </app-section>
            }
        </div>

        @if (Features.CheckpointsSave | hasFeature) {
            <app-sticky-save-bar (cancel)="cancel()" (save)="save()" [dirty]="formState().dirty" [saving]="saving" [valid]="formState().valid">
                @if (form().get('id').value && (Features.CheckpointsDetailDelete | hasFeature)) {
                    <ng-container *ngTemplateOutlet="deleteButton"></ng-container>
                }
            </app-sticky-save-bar>
        } @else {
            <div class="d-flex justify-content-end gap-2">
                @if (form().get('id').value && (Features.CheckpointsDetailDelete | hasFeature)) {
                    <ng-container *ngTemplateOutlet="deleteButton"></ng-container>
                }
                <button (click)="cancel()" mat-stroked-button type="button">Annuleren</button>
            </div>
        }
    }
</app-page>

<ng-template #deleteButton>
    <button (click)="openDelete($event)" class="ui-error" mat-button type="button">
        <i class="fa-regular fa-trash-can me-1"></i> Verwijderen
    </button>
</ng-template>
